import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import { authReducer } from "./Auth/Reducer/auth.reducer";
import { dashboardReducer } from "./Admin/Reducer/dashboard.reducer";
import { categoriesReducer } from "./Admin/Reducer/MasterData/categories.reducer";
import { assetsReducer } from "./Admin/Reducer/MasterData/assets.reducer";
import { materialReducer } from "./Admin/Reducer/MasterData/materials.reducer";
import { consumablesReducer } from "./Admin/Reducer/MasterData/consumables.reducer";
import { adminSettingsReducer } from "./Admin/Reducer/adminSetting.reducer";
import { hrModuleReducer } from "./Admin/Reducer/hrModule.reducer";
import { tasksReducer } from "./Admin/Reducer/MasterData/tasks.reducer";
import { servicesReducer } from "./Admin/Reducer/MasterData/services.reducer";
import { timesheetsReducer } from "./Admin/Reducer/timesheets.reducer";
import { leadsReducer } from "./Admin/Reducer/leads.reducer";
import { contactsReducer } from "./Admin/Reducer/contacts.reducer";
import { InventoryReducer } from "./Admin/Reducer/inventory.reducer";
import { estimateReducer } from "./Admin/Reducer/estimate.reducer";
import { quickBooksReducer } from "./Admin/Reducer/quickBooks.reducer";
import { customerEstimateReducer } from "./Customer/Reducer/customerEstimate.reducer";
import { customerLeadReducer } from "./Customer/Reducer/customerLead.reducer";
import { boatsReducer } from "./Admin/Reducer/boats.reducer";
import { emailReducer } from "./Admin/Reducer/emailTemplate.reducer";
import { staticReducer } from "./Admin/Reducer/static.reducer";
import filterReducer from "./helpers/slices/filtersSlice";
import { filter } from "lodash";
import { workOrderReducer } from "./Admin/Reducer/workOrder.reducer";
import { taskReducer } from "./Admin/Reducer/task.reducer";
import { InvoicesReducer } from "./Admin/Reducer/invoices.reducer";
import { StaticDataReducer } from "./Admin/Reducer/staticData.reducer";
import { scheduleReducer } from "./Admin/Reducer/schedule.reducer";
import { xeroReducer } from "./Admin/Reducer/xero.reducer";
// import { authReducer } from "./Auth/Redux/Reducers/auth";

// Error Reducer Types
interface ErrorState {
  authError?: Record<string, string>;
}

interface SetErrorActionPayload {
  scope: string;
  status: {
    param: string;
    msg: string;
    path?: string;
    nestedErrors?: { param: string; msg: string }[];
  }[];
}

interface SetErrorAction {
  type: "SET_ERROR";
  payload: SetErrorActionPayload;
}

type ErrorAction = SetErrorAction;

const errorReducer = (state: ErrorState = {}, action: ErrorAction): ErrorState => {
  switch (action.type) {
    case "SET_ERROR": {
      const temp: Record<string, string> = {};
      const { payload } = action;

      if (payload.status && payload.status.length) {
        payload.status.forEach((ele) => {
          if (ele.param === "_error") {
            ele.nestedErrors?.forEach((ele1) => {
              const key = ele1.param.split(".").pop()!;
              temp[key] = ele1.msg;
            });
          } else if (ele.param?.includes("updates.")) {
            const key = ele.param.split(".").slice(1).join(".");
            temp[key] = ele.msg;
          } else {
            temp[ele.path!] = ele.msg;
          }
        });
      }

      return { ...state, [`${payload.scope}Error`]: temp };
    }
    default:
      return state;
  }
};

// Loading Reducer Types
interface LoadingState {
  authLoading: boolean;
  welcomeDataLoading: boolean;
  [key: string]: boolean;
}

interface SetLoadingActionPayload {
  scope: string;
  status: boolean;
}

interface SetLoadingAction {
  type: "SET_LOADING";
  payload: SetLoadingActionPayload;
}

type LoadingAction = SetLoadingAction;

const loadingReducer = (
  state: LoadingState = { authLoading: false, welcomeDataLoading: false },
  action: LoadingAction
): LoadingState => {
  switch (action.type) {
    case "SET_LOADING":
      return {
        ...state,
        [`${action.payload.scope}Loading`]: action.payload.status,
      };
    default:
      return state;
  }
};

// Msg Reducer Types
interface MsgState {
  success?: string;
  error?: string;
  [key: string]: string | undefined;
}

interface SetToastActionPayload {
  scope: string;
  status?: string;
}

interface SetToastAction {
  type: "SET_TOAST";
  payload: SetToastActionPayload;
}

interface ClearToastAction {
  type: "CLEAR_TOAST";
}

type MsgAction = SetToastAction | ClearToastAction;

const msgReducer = (state: MsgState = {}, action: MsgAction): MsgState => {
  switch (action.type) {
    case "SET_TOAST":
      return {
        ...state,
        [`${action.payload.scope}`]: action.payload.status || "Something went wrong!",
      };
    case "CLEAR_TOAST":
      return { ...state, error: undefined, success: undefined };
    default:
      return state;
  }
};

// Combine reducers
const rootReducer = combineReducers({
  error: errorReducer,
  loading: loadingReducer,
  msg: msgReducer,

  auth: authReducer,
  dashboard: dashboardReducer,
  categories: categoriesReducer,
  assets: assetsReducer,
  material: materialReducer,
  consumables: consumablesReducer,
  adminSettings: adminSettingsReducer,
  hrModule: hrModuleReducer,
  tasks: tasksReducer,
  services: servicesReducer,
  timesheets: timesheetsReducer,
  leads: leadsReducer,
  contacts: contactsReducer,
  inventory: InventoryReducer,
  estimate: estimateReducer,
  quickBooks: quickBooksReducer,
  boats: boatsReducer,
  email: emailReducer,
  static: staticReducer,
  filters: filterReducer,
  workorder: workOrderReducer,
  workorderTask: taskReducer,
  invoices: InvoicesReducer,
  staticData: StaticDataReducer,
  schedule: scheduleReducer,
  customerLead: customerLeadReducer,
  customerEstimate: customerEstimateReducer,
  xero: xeroReducer,
});

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

// Configure store
const store = configureStore({
  reducer: rootReducer,
});

export default store;
